import type { ProviderType } from "@wags/catalog/pure.ts";
import type { ListingContactPreview } from "@wags/catalog/pure.ts";
import type { CompatibilityListingInput } from "@wags/matchmaker/pure.ts";
import {
    type ContactListingState,
    DEFAULT_CONTACT_MESSAGE_INTENT,
} from "@wags/profile-contracts/contact-session.contract.ts";
import {
    useIsMobileViewport,
    useUnsafeLayoutEffect,
} from "@wags/tanstack-start/react";
import { usePostHog } from "posthog-js/react";
import { useContactTextKits } from "@/features/contact-text-kits";
import {
    type ListingTransaction,
    openReachOutSheet,
    refreshAfterContactVerified,
    requestContactConfirmation,
    setDockAction,
} from "@/features/kits-conversations";
import {
    contactNeedsCompatibilityStep,
    useCompatibilityProfile,
    useViewerCompatibility,
} from "@/features/matching";
import {
    CONTACT_FUNNEL_EVENTS,
    asksAboutListing,
    captureContactFunnelEvent,
    contactFunnelProperties,
    useOneTapContact,
    useReachOutReviewStripEnabled,
} from "@/features/provider-outreach";
import { useAuth } from "@/lib/core/auth-protocol";
import type { MoneyRange } from "@/lib/ui/price";
import type { PrimaryAction } from "./dog-listing-actions";
import {
    type ContactIntent,
    type DockPresentation,
    REACH_OUT_LABEL,
    type ListingActionArea,
    listingTransaction,
} from "./dog-listing-presentation";

export type ContactOrigin = "inline" | "dock";

const OPENING_MESSAGES = "Opening Messages…";

const ATTEMPT_SOURCE: Record<ContactOrigin, string> = {
    inline: "inline_buy_row",
    dock: "pill_dock",
};

type KitsContactTransaction = Exclude<ListingTransaction, { kind: "link" }>;

function useListingContactAction({
    listingId,
    listingName,
    providerType,
    contactPreview,
    compatibility,
    transaction,
    listingState,
}: {
    listingId: string;
    listingName: string;
    providerType: ProviderType;
    contactPreview: ListingContactPreview;
    compatibility: CompatibilityListingInput;
    transaction: KitsContactTransaction | null;
    listingState: ContactListingState;
}) {
    const posthog = usePostHog();
    const intent =
        transaction?.kind === "contact"
            ? transaction.intent
            : DEFAULT_CONTACT_MESSAGE_INTENT;
    const textKits = useContactTextKits({
        dogName: transaction ? listingName : null,
        measured: transaction !== null,
    });
    const { authUserId, isLoaded, isSignedIn } = useAuth();
    const { profile, hydrated } = useCompatibilityProfile();
    const reviewStrip = useReachOutReviewStripEnabled();
    const reviewProfile = useViewerCompatibility(
        textKits.treatment && reviewStrip,
    ).data;
    const funnel = (source: string) =>
        contactFunnelProperties({
            listingId,
            providerType,
            source,
            isLoaded,
            isSignedIn,
            intent,
            listingState,
        });
    const oneTap = useOneTapContact({
        listingId,
        authUserId: authUserId ?? "anonymous",
        shared: funnel("one_tap_dock"),
        onVerified: refreshAfterContactVerified,
    });

    const openSheet = (handedOff: boolean, question?: string) =>
        openReachOutSheet(
            listingId,
            listingName,
            contactPreview,
            "reach_out_sheet",
            { intent, handedOff, question },
        );
    const recordAttempt = (origin: ContactOrigin) =>
        captureContactFunnelEvent(
            posthog,
            CONTACT_FUNNEL_EVENTS.attempted,
            funnel(ATTEMPT_SOURCE[origin]),
            { dogName: listingName, one_tap_available: oneTap.available },
        );
    const oneTapReady = () =>
        textKits.treatment &&
        isLoaded &&
        oneTap.available &&
        !(
            asksAboutListing(intent) &&
            contactNeedsCompatibilityStep({
                listing: compatibility,
                hydrated,
                profile,
                reviewProfile: reviewStrip ? reviewProfile : null,
            })
        );

    const run = (origin: ContactOrigin, question?: string) => {
        if (question) {
            recordAttempt(origin);
            openSheet(false, question);
            return;
        }
        if (oneTapReady()) {
            if (oneTap.open(openSheet, `one_tap_${origin}`))
                recordAttempt(origin);
            return;
        }
        recordAttempt(origin);
        openSheet(false);
    };

    return {
        busy: oneTap.pending,
        run,
        transaction:
            transaction?.kind === "price" && textKits.treatment
                ? { ...transaction, oneTap: true }
                : transaction,
    };
}

function useDockedContactAction(
    action: { run: () => void; busy: boolean } | null,
) {
    useUnsafeLayoutEffect(() => {
        setDockAction(action);
    });
    useUnsafeLayoutEffect(() => () => setDockAction(null), []);
}

export type InlineContactAction = Omit<PrimaryAction, "compactLabel"> & {
    busy: boolean;
};

export type StatusContactAction = PrimaryAction & { busy: boolean };

export function useDogContact({
    dogId,
    dogName,
    compatibility,
    providerType,
    contactPreview,
    action,
    dock,
    priceRange,
    listingState,
    copilotEnabled,
    ask,
}: {
    dogId: string;
    dogName: string;
    compatibility: CompatibilityListingInput;
    providerType: ProviderType;
    contactPreview: ListingContactPreview;
    action: ListingActionArea;
    dock: DockPresentation;
    priceRange: MoneyRange | null | undefined;
    listingState: ContactListingState;
    copilotEnabled: boolean;
    ask: { label: string; question: string } | null;
}) {
    const posthog = usePostHog();
    const { isLoaded, isSignedIn } = useAuth();
    const isMobileViewport = useIsMobileViewport();
    const base = copilotEnabled ? listingTransaction(action, priceRange) : null;
    const kits = useListingContactAction({
        listingId: dogId,
        listingName: dogName,
        providerType,
        contactPreview,
        compatibility,
        transaction: base,
        listingState,
    });
    const asked = copilotEnabled && action.kind === "buy" ? ask : null;
    useDockedContactAction(
        dock.kits && base
            ? { run: () => kits.run("dock", asked?.question), busy: kits.busy }
            : null,
    );

    const requestContact = (intent: ContactIntent) => {
        captureContactFunnelEvent(
            posthog,
            CONTACT_FUNNEL_EVENTS.attempted,
            contactFunnelProperties({
                listingId: dogId,
                providerType,
                source: "dog_detail",
                isLoaded,
                isSignedIn,
                intent,
                listingState,
            }),
            { dogName },
        );
        requestContactConfirmation(
            dogId,
            dogName,
            contactPreview,
            "dog_detail",
            intent,
        );
    };
    const label = asked
        ? asked.label
        : action.kind === "buy"
          ? action.label
          : REACH_OUT_LABEL;
    const primaryAction: PrimaryAction = {
        label,
        compactLabel: REACH_OUT_LABEL,
        accessibleLabel: label,
        onClick: asked
            ? () => kits.run("inline", asked.question)
            : () => requestContact("available"),
    };
    const inlineBuy: InlineContactAction = {
        label: kits.busy ? OPENING_MESSAGES : primaryAction.label,
        accessibleLabel: primaryAction.accessibleLabel,
        busy: kits.busy,
        onClick: copilotEnabled
            ? () => kits.run("inline", asked?.question)
            : primaryAction.onClick,
    };
    const contact = action.kind === "status" ? action.contact : null;
    const statusContact: StatusContactAction | null = contact && {
        label: kits.busy ? OPENING_MESSAGES : contact.label,
        accessibleLabel: contact.label,
        compactLabel: contact.compactLabel,
        busy: kits.busy,
        onClick:
            copilotEnabled && isMobileViewport
                ? () => kits.run("inline")
                : () => requestContact(contact.intent),
    };
    return {
        transaction: kits.transaction,
        primaryAction,
        inlineBuy,
        statusContact,
    };
}
